import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, HelpCircle, Search, MessageSquare, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const FAQ_GROUPS = [
  {
    category: "About CNESS",
    items: [
      {
        q: "What is CNESS Works?",
        a: "CNESS Works is a Conscious Enterprise Assessment & Certification platform. We help organisations evaluate their practices across six pillars and earn third-party certification that signals trust to customers, employees and investors.",
      },
      {
        q: "What does 'conscious enterprise' mean?",
        a: "A conscious enterprise is a company that operates with intentional awareness of its impact — on people, planet, and purpose. CNESS measures this through the Six-Pillar Framework: Conscious Leadership, Human-Centric Culture, Products & Services, Customers & Clients, Brand Vision, and Community Impact.",
      },
      {
        q: "Who is CNESS for?",
        a: "Any organisation — startup, SME, or enterprise — that wants to embed responsible practices into how it actually operates. Our three tiers (Aspiring, Inspired, Luminary) are designed to meet you where you are.",
      },
    ],
  },
  {
    category: "Certification Process",
    items: [
      {
        q: "How long does certification take?",
        a: "Most organisations complete certification within 30 days. The exact timeline depends on tier complexity and how prepared your documentation is.",
      },
      {
        q: "What are the three certification tiers?",
        a: "Aspiring (baseline foundation), Inspired (committed implementation with coaching), and Luminary (global leadership recognition). Choose the tier that matches your stage and aspirations.",
      },
      {
        q: "Is certification permanent?",
        a: "Certification is valid for one year and renewed annually. This ensures organisations continue improving and that the badge remains a credible, current signal of conscious practice.",
      },
      {
        q: "Can certification be revoked?",
        a: "Yes. If an organisation no longer meets the standards or breaches the CNESS code of conduct, certification can be suspended or revoked after a formal review.",
      },
    ],
  },
  {
    category: "Pricing & Payment",
    items: [
      {
        q: "How much does certification cost?",
        a: "Pricing varies by tier and organisation size. We share custom proposals after an initial discovery call. Submit an enquiry from the Certification page and our team will respond within 1 business day.",
      },
      {
        q: "Are there partner discounts?",
        a: "Yes. CNESS partners receive preferred rates and revenue-share arrangements for organisations they refer. See the Partner Program page for details.",
      },
    ],
  },
  {
    category: "Partner Program",
    items: [
      {
        q: "Who can become a CNESS partner?",
        a: "Consultants, advisory firms, media platforms, content creators, and technology platforms aligned with conscious business values. We have five partnership pathways tailored for different partner types.",
      },
      {
        q: "What revenue share do partners earn?",
        a: "Active partners earn up to 15% revenue share on referred organisations, with bonuses tied to volume and tier.",
      },
    ],
  },
  {
    category: "Platform & Technical",
    items: [
      {
        q: "Is the assessment online?",
        a: "Yes. The full 80-question assessment runs on our secure platform with progress saving, evaluator review, and a Conscious Impact Score (CIS) report.",
      },
      {
        q: "How is my data handled?",
        a: "Your assessment data is encrypted in transit and at rest. We never sell or share data. See our Privacy Policy for full details.",
      },
    ],
  },
];

const FAQsPage = () => {
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState("0-0");
  const [search, setSearch] = useState("");

  const filtered = FAQ_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (it) =>
        !search ||
        it.q.toLowerCase().includes(search.toLowerCase()) ||
        it.a.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((g) => g.items.length > 0);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#F8FAFC] to-white" data-testid="faqs-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <HelpCircle className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">Frequently Asked Questions</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1E3A5F] mb-6">
              Answers to <span className="text-[#FDB813]">common questions</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Everything you need to know about CNESS certification, partnerships, and the platform.
            </p>

            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                data-testid="faq-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search questions..."
                className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 focus:outline-none focus:border-[#FDB813] focus:ring-2 focus:ring-[#FDB813]/20 bg-white text-[#1E3A5F]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Groups */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto space-y-12">
            {filtered.length === 0 && (
              <p className="text-center text-gray-500" data-testid="faq-no-results">
                No questions matched "{search}". Try different keywords.
              </p>
            )}
            {filtered.map((group, gi) => (
              <div key={group.category} data-testid={`faq-group-${gi}`}>
                <h2 className="text-2xl font-bold text-[#1E3A5F] mb-6">{group.category}</h2>
                <div className="space-y-3">
                  {group.items.map((item, ii) => {
                    const key = `${gi}-${ii}`;
                    const isOpen = openKey === key;
                    return (
                      <div
                        key={key}
                        className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-sm transition-shadow"
                      >
                        <button
                          onClick={() => setOpenKey(isOpen ? null : key)}
                          data-testid={`faq-toggle-${gi}-${ii}`}
                          className="w-full flex items-center justify-between text-left p-5 sm:p-6"
                        >
                          <span className="font-semibold text-[#1E3A5F] pr-6">{item.q}</span>
                          <ChevronDown
                            className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                              isOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {isOpen && (
                          <div className="px-5 sm:px-6 pb-6 text-gray-600 leading-relaxed border-t border-gray-50 pt-4">
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-[#FEF3C7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MessageSquare className="w-12 h-12 text-[#1E3A5F] mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">Still have questions?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            Our team is happy to help. Reach out and we'll respond within 1 business day.
          </p>
          <Button
            onClick={() => navigate("/contact")}
            data-testid="faq-contact-btn"
            className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-4 text-lg rounded-xl"
          >
            Contact Us <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default FAQsPage;
